<template>
  <div class="content-video-slider">
    <div v-if="carouselData.posterimage || carouselData.postervideo" class="media-section">
      <!-- NOSONAR --><video
        v-if="carouselData.postervideo"
        :src="carouselData.postervideo"
        autoplay
        muted
        loop
        class="lazyload"
        playsinline
        preload="none"
      ></video>
      <!-- NOSONAR --><img
        v-else-if="carouselData.posterimage"
        :src="carouselData.posterimage"
        alt=""
        loading="lazy"
        class="lazyload"
      />
    </div>
    <div class="content-section">
      <div class="slider-header">
        <h2 class="slider-title">{{ truncateText(carouselData.title, 122) }}</h2>
        <p v-if="carouselData.description" class="slider-subtitle">
          {{ truncateText(carouselData.description, 224) }}
        </p>
      </div>

      <div class="slider-container">
        <div class="slider-controls">
          <button
            v-if="carouselData.ctatext"
            class="cta-button"
            @click="redirectTo(carouselData.ctalink)"
          >
            {{ truncateText(carouselData.ctatext, 32) }}
          </button>

          <div v-if="shouldShowNav" class="nav-buttons">
            <button
              type="button"
              class="btn-prev"
              :disabled="currentSlide === 0"
              aria-label="Previous slide"
              @click="prevSlide"
            >
              <svg
                width="5"
                height="9"
                viewBox="0 0 5 9"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M5 1.08636L4.21325 0.337105L0.205625 4.15273C0.153599 4.20158 0.112134 4.26057 0.0837897 4.32607C0.0554451 4.39157 0.0408214 4.46218 0.0408214 4.53354C0.0408214 4.60491 0.0554451 4.67552 0.0837897 4.74101C0.112134 4.80651 0.153599 4.8655 0.205625 4.91435L4.21325 8.72998L5 7.98073L1.379 4.53335L5 1.08636Z"
                  fill="white"
                />
              </svg>
            </button>

            <button
              type="button"
              class="btn-next"
              :disabled="currentSlide >= maxSlide"
              aria-label="Next slide"
              @click="nextSlide"
            >
              <svg
                width="5"
                height="9"
                viewBox="0 0 5 9"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M0 8.37361L0.78675 9.12286L4.79438 5.30723C4.8464 5.25838 4.88787 5.19939 4.91621 5.13389C4.94456 5.06839 4.95918 4.99778 4.95918 4.92642C4.95918 4.85505 4.94456 4.78444 4.91621 4.71895C4.88787 4.65345 4.8464 4.59446 4.79438 4.54561L0.78675 0.72998L0 1.47923L3.621 4.92661L0 8.37361Z"
                  fill="white"
                />
              </svg>
            </button>
          </div>
        </div>
        <carousel
          :key="carouselData.cards.length"
          ref="carousel"
          :per-page-custom="[
            [1440, 3.1],
            [1220, 3],
            [768, 2.1],
            [0, 1.1],
          ]"
          :scroll-per-page="false"
          :navigation-enabled="false"
          :pagination-enabled="false"
          :auto-play="false"
          class="slider-wrapper"
          @pageChange="onPageChange"
        >
          <slide v-for="(card, index) in processedCards" :key="index">
            <div
              class="slider-item"
              @touchstart.passive="handleTouchStart"
              @touchend="(e) => handleTouchEnd(e, card)"
              @mousedown="onMouseDown"
              @mouseup="(e) => onMouseUp(e, card)"
            >
              <div class="media-wrapper">
                <!-- video card-->
                <template v-if="card.videoCard">
                  <template v-if="card.cardimage">
                    <picture>
                      <source
                        media="(min-width: 768px)"
                        :srcset="card.cardimage"
                        type="image/webp"
                      />

                      <img
                        v-if="card.cardimage"
                        :src="card.cardimage"
                        alt=""
                        class="lazyload"
                        decoding="async"
                      />
                    </picture>

                    <span v-if="card.isVideoCardImage" class="button-pills --small-icon">
                      <svg
                        width="17"
                        height="17"
                        viewBox="0 0 17 17"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.48 8.72998L5.47998 14.23V3.22998L13.48 8.72998Z"
                          fill="white"
                          stroke="#242424"
                          stroke-width="0.5"
                          stroke-linejoin="round"
                        />
                      </svg>
                      <span class="is-sr-only">Play the video</span>
                    </span>
                  </template>
                  <template v-else>
                    <div v-intersect="() => (card.showVideo = true)" class="video-wrapper">
                      <!-- NOSONAR --><iframe
                        v-if="isYouTube(card.videoCard ? card.videoCard.videourl : '')"
                        :src="getYouTubeEmbedUrl(card.videoCard.videourl)"
                        title="Video player"
                        frameborder="0"
                        allowfullscreen
                      >
                      </iframe>
                      <!-- NOSONAR --><video
                        v-else-if="card.videoCard.videourl"
                        :src="card.videoCard.videourl"
                        autoplay
                        muted
                        loop
                        class="lazyload"
                        playsinline
                        preload="none"
                      ></video>
                    </div>
                  </template>
                </template>
                <!-- content card-->
                <template v-else-if="card.contentCard">
                  <div
                    v-if="card.cardmp4"
                    v-intersect="() => (card.showVideo = true)"
                    class="video-wrapper"
                  >
                    <!-- NOSONAR --><video
                      :src="card.cardmp4"
                      autoplay
                      muted
                      loop
                      class="lazyload"
                      playsinline
                      preload="none"
                    ></video>
                  </div>
                  <picture v-else-if="card.cardimage">
                    <source media="(min-width: 768px)" :srcset="card.cardimage" type="image/webp" />
                    <img
                      v-if="card.cardimage"
                      :src="card.cardimage"
                      alt=""
                      decoding="async"
                      class="lazyload"
                    />
                  </picture>
                </template>
              </div>
              <h3>{{ truncateText(card.title, 34) }}</h3>
              <p>{{ truncateText(card.subtitle, 121) }}</p>

              <button
                class="cta-button"
                @click.stop="handleButtonClick(card)"
                @mousedown.stop
                @mouseup.stop
                @touchstart.stop
                @touchend.stop
                @keydown="handleKeydown"
              >
                {{ truncateText(card.ctalabel, 32) }}
              </button>
            </div>
          </slide>
        </carousel>
      </div>
    </div>
    <video-popup
      v-if="showVideoPopup"
      :video-url="popupVideoUrl"
      :is-open="showVideoPopup"
      @close="closeVideoPopup"
    />
  </div>
</template>

<script>
import carousel from '../carousel/carousel.vue';
import slide from '../carousel/slide.vue';
import VideoPopup from '../video-popup/video-popup.vue';

export default {
  name: 'content-video-slider',
  components: { carousel, slide, VideoPopup },
  directives: {
    intersect: {
      inserted(el, binding) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              binding.value();
              observer.unobserve(el);
            }
          },
          { threshold: 0.1 },
        );
        observer.observe(el);
      },
    },
  },
  props: {
    carouselData: Object,
  },
  data() {
    return {
      showVideoPopup: false,
      popupVideoUrl: '',
      currentSlide: 0,
      touchStartX: 0,
      touchStartY: 0,
      touchThreshold: 10,
      isTaggingDone: false,
    };
  },
  computed: {
    processedCards() {
      return this.carouselData.cards.map((card) => {
        const content = card.contentCard || card.videoCard || {};
        return {
          videoCard: card.videoCard ? card.videoCard : null,
          contentCard: card.contentCard ? card.contentCard : null,
          videoUrl: card.videoCard ? card.videoCard.videourl : null,
          cardmp4: card.contentCard ? card.contentCard.cardmp4 : null,
          title: content.title || '',
          subtitle: content.subtitle || '',
          cardimage: content.cardimage || '',
          ctalabel: content.ctalabel || '',
          ctalink: content.ctalink || '',
          isVideoCardImage: card.videoCard && card.videoCard.cardimage ? true : false,
          cardTagging: card.contentCard
            ? card.contentCard.contentcardtagging
            : card.videoCard
              ? card.videoCard.videocardtagging
              : null,
          showVideo: false,
        };
      });
    },
    perPage() {
      const width = window.innerWidth;
      if (width >= 1440) return 3.1;
      if (width >= 1220) return 3;
      if (width >= 768) return 2.1;
      return 1.1;
    },

    maxSlide() {
      return Math.ceil(this.carouselData.cards.length - this.perPage);
    },

    shouldShowNav() {
      return this.carouselData.cards.length > Math.floor(this.perPage);
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.handleResize(); // run on mount
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
    },

    handleTouchEnd(e, card) {
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;

      const diffX = Math.abs(touchEndX - this.touchStartX);
      const diffY = Math.abs(touchEndY - this.touchStartY);

      if (diffX < this.touchThreshold && diffY < this.touchThreshold) {
        this.onCardClick(card);
      }
    },
    onMouseDown(e) {
      this.touchStartX = e.clientX;
      this.touchStartY = e.clientY;
    },
    onMouseUp(e, card) {
      const diffX = Math.abs(e.clientX - this.touchStartX);
      const diffY = Math.abs(e.clientY - this.touchStartY);

      if (diffX < this.touchThreshold && diffY < this.touchThreshold) {
        this.onCardClick(card);
      }
    },
    onPageChange(newPage) {
      this.currentSlide = newPage;
    },
    handleResize() {
      this.$forceUpdate();
    },
    isYouTube: (url) => url && (url.includes('youtube.com') || url.includes('youtu.be')),
    getYouTubeEmbedUrl(url) {
      return url.replace('watch?v=', 'embed/');
    },
    truncateText(text, maxLength) {
      return text && text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
    },
    redirectTo(url) {
      if (url) window.location.href = url;
    },
    openVideoPopup(videoUrl) {
      if (!videoUrl) return;
      if (this.isYouTube(videoUrl)) {
        videoUrl = this.getYouTubeEmbedUrl(videoUrl);
      }
      this.popupVideoUrl = videoUrl;
      this.showVideoPopup = true;
      document.body.style.overflow = 'hidden';
    },
    closeVideoPopup() {
      this.showVideoPopup = false;
      document.body.style.overflow = '';
    },

    getSlidesPerPage() {
      const width = window.innerWidth;
      if (width >= 1440) return 3.1;
      if (width >= 1220) return 3;
      if (width >= 768) return 2.1;
      return 1.1;
    },

    prevSlide() {
      if (this.$refs.carousel) {
        this.currentSlide = Math.max(this.currentSlide - 1, 0);
        this.$refs.carousel.goToPage(this.currentSlide);
      }
    },

    nextSlide() {
      if (this.$refs.carousel) {
        const perPage = this.getSlidesPerPage();
        const totalItems = this.carouselData.cards.length;

        // Calculate maximum number of "full moves" based on fractional per page
        const maxSlide = Math.ceil(totalItems - perPage);
        this.currentSlide = Math.min(this.currentSlide + 1, maxSlide);
        this.$refs.carousel.goToPage(this.currentSlide);
      }
    },
    cardTagging(card, isButtonClick = false) {
      if (this.isTaggingDone) {
        return;
      }
      try {
        this.isTaggingDone = true;
        const Json = card.cardTagging;
        const fixedJson = Json.replace(/&#39;/g, '"');
        let validJsonString = fixedJson
          // eslint-disable-next-line sonarjs/super-linear-regex -- preserve JSON repair behavior
          .replace(/(\w+):/g, '"$1":') // Add double quotes around property names
          .replace(/'([^']+)'/g, '"$1"')
          .replace(/""https":\/\//, '"https://')

          .replace(/:\s*"([^"]*)"/g, (_, value) => `: "${value.trim()}"`);

        const taggingData = JSON.parse(validJsonString);

        if (taggingData) {
          const cardClickTag = {
            event: taggingData.event,
            ecommerce: taggingData.ecommerce,
            event_name: taggingData.event_name,
            cta_name: taggingData.cta_name,
            link_url: isButtonClick || card.contentCard ? taggingData.link_url : 'none',
            module_name: taggingData.module_name,
          };
          window.dataLayer.push(cardClickTag);
        }
      } catch (error) {
        console.error('Tagging error:', error);
      } finally {
        // Reset the flag after a short delay
        setTimeout(() => {
          this.isTaggingDone = false;
        }, 300);
      }
    },

    handleKeydown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.stopPropagation();
      }
    },

    handleButtonClick(card) {
      this.cardTagging(card, true);
      this.redirectTo(card.ctalink);
    },

    onCardClick(card) {
      this.cardTagging(card);
      if (card.videoCard) {
        this.openVideoPopup(card.videoUrl);
      } else if (card.contentCard) {
        this.redirectTo(card.ctalink);
      }
    },
  },
};
</script>
